import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Alert, Table } from "reactstrap";
import { FaPlus, FaMinus, FaEdit, FaTrash, FaBox, FaChartLine } from "react-icons/fa";
import axios from "axios";
import { getApiUrl } from "../../utils/site";
import "../../styles/adminGlassStyle.js";

const InventoryManagement = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Modals
  const [addModal, setAddModal] = useState(false);
  const [deductModal, setDeductModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  
  // Form data
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [formData, setFormData] = useState({
    material_type: "",
    total_grams: "",
    grams: "",
    unit: "grams"
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl("/api/inventory"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data.data || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInventory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        getApiUrl("/api/inventory"),
        {
          material_type: formData.material_type,
          total_grams: parseFloat(formData.total_grams),
          unit: formData.unit
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Inventory created successfully!");
      setCreateModal(false);
      setFormData({ material_type: "", total_grams: "", grams: "", unit: "grams" });
      fetchInventory();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create inventory");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleAddToInventory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        getApiUrl("/api/inventory/add"),
        {
          material_type: selectedMaterial.material_type,
          grams: parseFloat(formData.grams)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Added ${formData.grams}g to ${selectedMaterial.material_type}`);
      setAddModal(false);
      setFormData({ ...formData, grams: "" });
      fetchInventory();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add to inventory");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleDeductFromInventory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        getApiUrl("/api/inventory/deduct"),
        {
          material_type: selectedMaterial.material_type,
          grams: parseFloat(formData.grams)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Deducted ${formData.grams}g from ${selectedMaterial.material_type}`);
      setDeductModal(false);
      setFormData({ ...formData, grams: "" });
      fetchInventory();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to deduct from inventory");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleDeleteInventory = async (materialType) => {
    if (!window.confirm(`Are you sure you want to delete ${materialType} inventory?`)) return;
    
    try {
      await axios.delete(getApiUrl(`/api/inventory/${materialType}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Inventory deleted successfully!");
      fetchInventory();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete inventory");
      setTimeout(() => setError(""), 3000);
    }
  };

  const openAddModal = (material) => {
    setSelectedMaterial(material);
    setAddModal(true);
  };

  const openDeductModal = (material) => {
    setSelectedMaterial(material);
    setDeductModal(true);
  };

  const getStockStatus = (grams) => {
    if (grams <= 0) return { color: "danger", text: "Out of Stock" };
    if (grams < 500) return { color: "warning", text: "Low Stock" };
    return { color: "success", text: "In Stock" };
  };

  const totalInventoryValue = inventory.reduce((sum, item) => sum + item.total_grams, 0);

  return (
    <div className="admin-page" style={{ minHeight: "100vh", background: "var(--color-dark)", padding: "2rem 0" }}>
      <Container>
        <div className="admin-header" style={{ marginBottom: "2rem" }}>
          <div>
            <h1 style={{ color: "var(--color-heading)", fontFamily: "var(--font-heading)", fontSize: "2.5rem", marginBottom: "0.5rem" }}>
              Inventory Management
            </h1>
            <p style={{ color: "var(--color-muted)" }}>
              Track and manage material inventory for jewellery production
            </p>
          </div>
          <Button
            color="primary"
            onClick={() => setCreateModal(true)}
            style={{
              background: "linear-gradient(135deg, #c9a84c, #e8c875)",
              border: "none",
              color: "#0a1a18",
              fontWeight: "700",
              padding: "0.75rem 1.5rem",
              borderRadius: "999px"
            }}
          >
            <FaBox /> Create New Inventory
          </Button>
        </div>

        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md="4">
            <Card className="admin-glass-card">
              <CardBody>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(201,168,76,0.2), rgba(201,168,76,0.1))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    color: "var(--color-gold)"
                  }}>
                    <FaBox />
                  </div>
                  <div>
                    <p style={{ color: "var(--color-muted)", fontSize: "0.85rem", margin: 0 }}>Total Materials</p>
                    <h3 style={{ color: "var(--color-heading)", margin: 0 }}>{inventory.length}</h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="admin-glass-card">
              <CardBody>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(42,157,120,0.2), rgba(42,157,120,0.1))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    color: "#2a9d78"
                  }}>
                    <FaChartLine />
                  </div>
                  <div>
                    <p style={{ color: "var(--color-muted)", fontSize: "0.85rem", margin: 0 }}>Total Inventory</p>
                    <h3 style={{ color: "var(--color-heading)", margin: 0 }}>{totalInventoryValue.toFixed(2)}g</h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="admin-glass-card">
              <CardBody>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(52,152,219,0.2), rgba(52,152,219,0.1))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    color: "#3498db"
                  }}>
                    <FaBox />
                  </div>
                  <div>
                    <p style={{ color: "var(--color-muted)", fontSize: "0.85rem", margin: 0 }}>Low Stock Items</p>
                    <h3 style={{ color: "var(--color-heading)", margin: 0 }}>
                      {inventory.filter(item => item.total_grams < 500).length}
                    </h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Inventory Table */}
        <Card className="admin-glass-card">
          <CardBody>
            {loading ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "var(--color-muted)" }}>
                Loading inventory...
              </div>
            ) : inventory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "var(--color-muted)" }}>
                No inventory found. Create your first inventory item.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table responsive style={{ color: "var(--color-text)" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-gold)" }}>
                      <th>Material Type</th>
                      <th>Total Grams</th>
                      <th>Unit</th>
                      <th>Status</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((item) => {
                      const status = getStockStatus(item.total_grams);
                      return (
                        <tr key={item.id} style={{ borderBottom: "1px solid rgba(201,168,76,0.1)" }}>
                          <td>
                            <strong style={{ color: "var(--color-heading)" }}>{item.material_type}</strong>
                          </td>
                          <td>
                            <span style={{ fontSize: "1.1rem", fontWeight: "600", color: "var(--color-gold)" }}>
                              {item.total_grams.toFixed(2)}
                            </span>
                          </td>
                          <td>{item.unit}</td>
                          <td>
                            <span style={{
                              padding: "0.25rem 0.75rem",
                              borderRadius: "999px",
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              background: status.color === "success" ? "rgba(42,157,120,0.15)" :
                                         status.color === "warning" ? "rgba(255,193,7,0.15)" :
                                         "rgba(220,53,69,0.15)",
                              color: status.color === "success" ? "#2a9d78" :
                                     status.color === "warning" ? "#ffc107" :
                                     "#dc3545"
                            }}>
                              {status.text}
                            </span>
                          </td>
                          <td style={{ color: "var(--color-muted)", fontSize: "0.85rem" }}>
                            {new Date(item.updated_at).toLocaleDateString()}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              <Button
                                size="sm"
                                color="success"
                                onClick={() => openAddModal(item)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaPlus /> Add
                              </Button>
                              <Button
                                size="sm"
                                color="warning"
                                onClick={() => openDeductModal(item)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaMinus /> Deduct
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => handleDeleteInventory(item.material_type)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Create Inventory Modal */}
        <Modal isOpen={createModal} toggle={() => setCreateModal(false)} className="admin-modal">
          <ModalHeader toggle={() => setCreateModal(false)} style={{ background: "var(--color-dark)", color: "var(--color-heading)", borderBottom: "1px solid var(--border-gold)" }}>
            Create New Inventory
          </ModalHeader>
          <ModalBody style={{ background: "var(--color-dark)", color: "var(--color-text)" }}>
            <Form onSubmit={handleCreateInventory}>
              <FormGroup>
                <Label>Material Type *</Label>
                <Input
                  type="text"
                  value={formData.material_type}
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                  placeholder="e.g., Local Silver, Foreign Silver, Gold"
                  required
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--color-heading)"
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Total Grams *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_grams}
                  onChange={(e) => setFormData({ ...formData, total_grams: e.target.value })}
                  placeholder="e.g., 2500"
                  required
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--color-heading)"
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Unit</Label>
                <Input
                  type="select"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--color-heading)"
                  }}
                >
                  <option value="grams">Grams</option>
                  <option value="kg">Kilograms</option>
                  <option value="oz">Ounces</option>
                </Input>
              </FormGroup>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <Button type="button" color="secondary" onClick={() => setCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" style={{
                  background: "linear-gradient(135deg, #c9a84c, #e8c875)",
                  border: "none",
                  color: "#0a1a18"
                }}>
                  Create Inventory
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        {/* Add to Inventory Modal */}
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} className="admin-modal">
          <ModalHeader toggle={() => setAddModal(false)} style={{ background: "var(--color-dark)", color: "var(--color-heading)", borderBottom: "1px solid var(--border-gold)" }}>
            Add to {selectedMaterial?.material_type}
          </ModalHeader>
          <ModalBody style={{ background: "var(--color-dark)", color: "var(--color-text)" }}>
            <Form onSubmit={handleAddToInventory}>
              <FormGroup>
                <Label>Current Stock</Label>
                <div style={{
                  padding: "1rem",
                  background: "rgba(42,157,120,0.1)",
                  border: "1px solid rgba(42,157,120,0.3)",
                  borderRadius: "8px",
                  marginBottom: "1rem"
                }}>
                  <strong style={{ fontSize: "1.5rem", color: "var(--color-gold)" }}>
                    {selectedMaterial?.total_grams.toFixed(2)} {selectedMaterial?.unit}
                  </strong>
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Add Grams *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.grams}
                  onChange={(e) => setFormData({ ...formData, grams: e.target.value })}
                  placeholder="Enter grams to add"
                  required
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--color-heading)"
                  }}
                />
              </FormGroup>
              {formData.grams && (
                <div style={{
                  padding: "1rem",
                  background: "rgba(201,168,76,0.1)",
                  border: "1px solid rgba(201,168,76,0.3)",
                  borderRadius: "8px",
                  marginBottom: "1rem"
                }}>
                  <p style={{ margin: 0, color: "var(--color-muted)" }}>New Total:</p>
                  <strong style={{ fontSize: "1.3rem", color: "var(--color-gold)" }}>
                    {(parseFloat(selectedMaterial?.total_grams || 0) + parseFloat(formData.grams || 0)).toFixed(2)} {selectedMaterial?.unit}
                  </strong>
                </div>
              )}
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <Button type="button" color="secondary" onClick={() => setAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="success">
                  <FaPlus /> Add to Inventory
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        {/* Deduct from Inventory Modal */}
        <Modal isOpen={deductModal} toggle={() => setDeductModal(false)} className="admin-modal">
          <ModalHeader toggle={() => setDeductModal(false)} style={{ background: "var(--color-dark)", color: "var(--color-heading)", borderBottom: "1px solid var(--border-gold)" }}>
            Deduct from {selectedMaterial?.material_type}
          </ModalHeader>
          <ModalBody style={{ background: "var(--color-dark)", color: "var(--color-text)" }}>
            <Form onSubmit={handleDeductFromInventory}>
              <FormGroup>
                <Label>Current Stock</Label>
                <div style={{
                  padding: "1rem",
                  background: "rgba(42,157,120,0.1)",
                  border: "1px solid rgba(42,157,120,0.3)",
                  borderRadius: "8px",
                  marginBottom: "1rem"
                }}>
                  <strong style={{ fontSize: "1.5rem", color: "var(--color-gold)" }}>
                    {selectedMaterial?.total_grams.toFixed(2)} {selectedMaterial?.unit}
                  </strong>
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Deduct Grams *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.grams}
                  onChange={(e) => setFormData({ ...formData, grams: e.target.value })}
                  placeholder="Enter grams to deduct"
                  required
                  max={selectedMaterial?.total_grams}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--border-gold)",
                    color: "var(--color-heading)"
                  }}
                />
              </FormGroup>
              {formData.grams && (
                <div style={{
                  padding: "1rem",
                  background: parseFloat(formData.grams) > parseFloat(selectedMaterial?.total_grams || 0) 
                    ? "rgba(220,53,69,0.1)" 
                    : "rgba(201,168,76,0.1)",
                  border: parseFloat(formData.grams) > parseFloat(selectedMaterial?.total_grams || 0)
                    ? "1px solid rgba(220,53,69,0.3)"
                    : "1px solid rgba(201,168,76,0.3)",
                  borderRadius: "8px",
                  marginBottom: "1rem"
                }}>
                  <p style={{ margin: 0, color: "var(--color-muted)" }}>New Total:</p>
                  <strong style={{ 
                    fontSize: "1.3rem", 
                    color: parseFloat(formData.grams) > parseFloat(selectedMaterial?.total_grams || 0) 
                      ? "#dc3545" 
                      : "var(--color-gold)" 
                  }}>
                    {(parseFloat(selectedMaterial?.total_grams || 0) - parseFloat(formData.grams || 0)).toFixed(2)} {selectedMaterial?.unit}
                  </strong>
                  {parseFloat(formData.grams) > parseFloat(selectedMaterial?.total_grams || 0) && (
                    <p style={{ margin: "0.5rem 0 0 0", color: "#dc3545", fontSize: "0.85rem" }}>
                      ⚠️ Insufficient inventory!
                    </p>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <Button type="button" color="secondary" onClick={() => setDeductModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  color="warning"
                  disabled={parseFloat(formData.grams) > parseFloat(selectedMaterial?.total_grams || 0)}
                >
                  <FaMinus /> Deduct from Inventory
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default InventoryManagement;
